import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { TikTokLogo } from './BrandLogos';
import {
  CheckCircle2,
  Sparkles,
  Clock,
  Search,
  UtensilsCrossed,
  TrendingDown,
  BarChart3,
  ArrowRight,
  ShieldCheck,
  Cpu,
  Globe,
  Truck,
  CalendarCheck,
  CreditCard,
} from 'lucide-react';
import { useT } from '../i18n';
import {
  ToastMark,
  CloverMark,
  SquareMark,
  LightspeedMark,
  GoogleMark,
  MetaMark,
  DoorDashMark,
  UberEatsMark,
  EzCaterMark,
  StripeMark,
  OpenTableMark,
} from './BrandIcons';

/* =================================================================
   Trusted — Restaurant Technology Platform integrations surface.

   Goals (positioning phase, NOT a redesign):
   - Reorganize the 22 integrations into 5 business-function categories
     so a restaurant operator instantly sees where Kitxens fits.
   - Replace generic "Connected/Supported" pills with enterprise
     SaaS language: Native / Managed / Certified.
   - Reinforce: "Works with the restaurant technology you already use."
   ================================================================= */

// Brands with first-party marks
const MARK = {
  toast: ToastMark,
  square: SquareMark,
  clover: CloverMark,
  lightspeed: LightspeedMark,
  stripe: StripeMark,
  opentable: OpenTableMark,
  google: GoogleMark,
  googlemaps: GoogleMark,
  meta: MetaMark,
  doordash: DoorDashMark,
  ubereats: UberEatsMark,
  ezcater: EzCaterMark,
};

// Brands without a dedicated SVG — rendered as a polished letterform
function LetterMark({ name }) {
  const initial = name.replace(/[^A-Za-z]/g, '').charAt(0).toUpperCase() || '·';
  return (
    <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-neutral-100 to-neutral-50 border border-neutral-200 flex items-center justify-center text-neutral-700 font-bold text-[15px] tracking-[-0.01em]">
      {initial}
    </span>
  );
}

// Integration model:
//   native    → first-party connector built and maintained by Kitxens
//   managed   → Kitxens operates the integration through certified APIs / middleware
//   certified → tested and supported through standard APIs / webhooks / partner connectors
//
// Categories map to how restaurant operators (and SaaS investors) think
// about their business — not how software vendors slice themselves.
const CATEGORIES = [
  {
    id: 'operations',
    icon: Cpu,
    brands: [
      { id: 'toast', name: 'Toast', status: 'native' },
      { id: 'square', name: 'Square', status: 'native' },
      { id: 'clover', name: 'Clover', status: 'native' },
      { id: 'lightspeed', name: 'Lightspeed', status: 'native' },
      { id: 'spoton', name: 'SpotOn', status: 'managed' },
      { id: 'revel', name: 'Revel', status: 'managed' },
    ],
  },
  {
    id: 'visibility',
    icon: Globe,
    brands: [
      { id: 'google', name: 'Google Business Profile', status: 'native' },
      { id: 'googlemaps', name: 'Google Maps', status: 'native' },
      { id: 'meta', name: 'Meta', status: 'native' },
      { id: 'tiktok', name: 'TikTok', status: 'certified' },
      { id: 'yelp', name: 'Yelp', status: 'certified' },
    ],
  },
  {
    id: 'delivery',
    icon: Truck,
    brands: [
      { id: 'doordash', name: 'DoorDash', status: 'native' },
      { id: 'ubereats', name: 'Uber Eats', status: 'native' },
      { id: 'grubhub', name: 'Grubhub', status: 'managed' },
      { id: 'chownow', name: 'ChowNow', status: 'managed' },
      { id: 'olo', name: 'Olo', status: 'managed' },
    ],
  },
  {
    id: 'reservations',
    icon: CalendarCheck,
    brands: [
      { id: 'opentable', name: 'OpenTable', status: 'native' },
      { id: 'resy', name: 'Resy', status: 'managed' },
      { id: 'ezcater', name: 'ezCater', status: 'native' },
    ],
  },
  {
    id: 'payments',
    icon: CreditCard,
    brands: [
      { id: 'stripe', name: 'Stripe', status: 'native' },
    ],
  },
];

// Outcome icons (5 cards keyed by index)
const OUTCOME_ICONS = [Clock, Search, UtensilsCrossed, TrendingDown, BarChart3];

// Official logo sources (brand domains) with graceful fallback to inline marks.
const DOMAINS = {
  toast: 'toasttab.com',
  square: 'squareup.com',
  clover: 'clover.com',
  lightspeed: 'lightspeedhq.com',
  spoton: 'spoton.com',
  revel: 'revelsystems.com',
  google: 'google.com',
  googlemaps: 'google.com',
  meta: 'meta.com',
  tiktok: 'tiktok.com',
  yelp: 'yelp.com',
  doordash: 'doordash.com',
  ubereats: 'ubereats.com',
  grubhub: 'grubhub.com',
  chownow: 'chownow.com',
  olo: 'olo.com',
  opentable: 'opentable.com',
  resy: 'resy.com',
  ezcater: 'ezcater.com',
  stripe: 'stripe.com',
};

function BrandLogo({ id, name }) {
  const [err, setErr] = useState(false);
  const domain = DOMAINS[id];
  const M = MARK[id];
  if (id === 'tiktok') return <TikTokLogo className="w-8 h-8" />;
  if (err || !domain) return M ? <M className="w-9 h-9" /> : <LetterMark name={name} />;
  return (
    <img
      src={`https://www.google.com/s2/favicons?domain=${domain}&sz=128`}
      alt={`${name} logo`}
      loading="lazy"
      decoding="async"
      onError={() => setErr(true)}
      className="w-9 h-9 rounded-xl object-contain bg-white ring-1 ring-neutral-100 p-1"
    />
  );
}

export default function Trusted() {
  const { t, lang } = useT();
  const outcomes = t('outcomes') || [];
  const categoriesMeta = t('trusted.categories') || [];

  const statusLabels = {
    native: t('trusted.statusNative') || 'Native Integration',
    managed: t('trusted.statusManaged') || 'Managed Integration',
    certified: t('trusted.statusCertified') || 'Certified Compatibility',
  };
  const statusTone = {
    native: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    managed: 'bg-orange-50 text-[#F77F11] border-orange-200',
    certified: 'bg-neutral-100 text-neutral-600 border-neutral-200',
  };
  const statusShort = {
    native: lang === 'es' ? 'Nativa' : 'Native',
    managed: lang === 'es' ? 'Gestionada' : 'Managed',
    certified: lang === 'es' ? 'Certificada' : 'Certified',
  };

  return (
    <section className="py-20 lg:py-24 bg-white" data-testid="trusted-block">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        {/* Heading */}
        <div className="max-w-3xl">
          <h2 className="text-3xl sm:text-4xl lg:text-[44px] font-bold text-neutral-900 tracking-[-0.02em] leading-[1.1]">
            {t('trusted.heading')}{' '}
            <span className="bg-gradient-to-r from-[#F77F11] to-[#ff9b4a] bg-clip-text text-transparent pb-[0.12em] -mb-[0.12em]">
              {t('trusted.headingAccent')}
            </span>
          </h2>
          <p className="mt-5 text-neutral-600 text-base sm:text-lg leading-relaxed max-w-2xl">
            {t('trusted.body')}
          </p>
        </div>

        {/* Compatibility label strip */}
        <div className="mt-12 flex items-center gap-3 flex-wrap">
          <p className="kx-eyebrow">
            {t('trusted.label')} <span className="text-neutral-900">{t('trusted.labelAccent')}</span>
          </p>
        </div>

        {/* Dual counter-scrolling brand marquees — every system, in motion */}
        <div className="mt-10 space-y-4 group" data-testid="integration-marquee">
          {[0, 1].map((row) => {
            const brands = CATEGORIES.flatMap((c) => c.brands).filter((_, i) => i % 2 === row);
            return (
              <div
                key={row}
                className="relative overflow-hidden [mask-image:linear-gradient(90deg,transparent,black_8%,black_92%,transparent)]"
              >
                <div className={`flex w-max py-1 ${row === 0 ? 'kx-marquee-slow' : 'kx-marquee-slow-reverse'}`}>
                  {[...brands, ...brands].map((b, i) => {
                    return (
                      <div
                        key={`${b.id}-${i}`}
                        className="mx-2.5 flex items-center gap-2.5 rounded-full border border-neutral-200 bg-white pl-2.5 pr-5 h-14 hover:border-[#F77F11] hover:shadow-[0_16px_44px_-20px_rgba(247,127,17,0.5)] hover:-translate-y-0.5 transition-[border-color,box-shadow,transform] duration-300"
                        data-testid={i < brands.length ? `integration-${b.id}` : undefined}
                        title={`${b.name} · ${statusLabels[b.status]}`}
                      >
                        <span className="shrink-0"><BrandLogo id={b.id} name={b.name} /></span>
                        <span className="text-[14.5px] font-semibold text-neutral-800 whitespace-nowrap">{b.name}</span>
                        <span
                          className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                            b.status === 'native' ? 'bg-emerald-500' : b.status === 'managed' ? 'bg-[#F77F11]' : 'bg-neutral-300'
                          }`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          <p className="text-[12.5px] text-neutral-500 text-center pt-1" data-testid="integration-legend">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1.5 align-middle" />
            {statusShort.native}
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#F77F11] ml-4 mr-1.5 align-middle" />
            {statusShort.managed}
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-neutral-300 ml-4 mr-1.5 align-middle" />
            {statusShort.certified}
          </p>
        </div>

        {/* What we connect → what happens — direct and concrete */}
        <div className="mt-14 grid md:grid-cols-[1fr_auto_1fr_auto_1fr] gap-4 items-stretch" data-testid="connect-flow">
          {[
            {
              icon: Cpu,
              t: lang === 'es' ? '1 · Conectamos' : '1 · We connect',
              b:
                lang === 'es'
                  ? 'Tu POS, delivery, reservas, pagos y canales — 40+ sistemas, sin cambiar nada de lo que ya usas.'
                  : 'Your POS, delivery, reservations, payments and channels — 40+ systems, without replacing anything you already use.',
            },
            {
              icon: Sparkles,
              t: lang === 'es' ? '2 · Aprende' : '2 · It learns',
              b:
                lang === 'es'
                  ? 'Cada pedido, reseña y búsqueda alimenta una inteligencia que conoce tu negocio mejor cada semana.'
                  : 'Every order, review and search feeds one intelligence that knows your business better every week.',
            },
            {
              icon: BarChart3,
              t: lang === 'es' ? '3 · Vendes más' : '3 · You sell more',
              b:
                lang === 'es'
                  ? 'Más visibilidad, más pedidos directos y decisiones con datos — medido contra tu propia línea base.'
                  : 'More visibility, more direct orders and data-backed decisions — measured against your own baseline.',
            },
          ].flatMap((s, i, arr) => {
            const card = (
              <motion.div
                key={s.t}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.45, delay: i * 0.12 }}
                className="rounded-2xl border border-neutral-200 bg-neutral-50/60 p-6 hover:border-[#F77F11]/60 hover:bg-orange-50/40 transition-colors"
              >
                <span className="inline-flex w-11 h-11 rounded-xl bg-white border border-orange-100 items-center justify-center shadow-sm">
                  <s.icon className="w-5 h-5 text-[#F77F11]" />
                </span>
                <h3 className="mt-4 text-[17px] font-bold text-neutral-900 tracking-[-0.005em]">{s.t}</h3>
                <p className="mt-2 text-[14px] text-neutral-600 leading-relaxed">{s.b}</p>
              </motion.div>
            );
            if (i < arr.length - 1) {
              return [
                card,
                <div key={`arrow-${i}`} className="hidden md:flex items-center justify-center">
                  <ArrowRight className="w-5 h-5 text-[#F77F11]" />
                </div>,
              ];
            }
            return [card];
          })}
        </div>

        {/* Trust + reassurance pair */}
        <div className="mt-10 grid lg:grid-cols-2 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.4 }}
            className="flex items-start gap-3 rounded-2xl border border-neutral-200 bg-neutral-50/60 px-5 py-4"
            data-testid="trusted-trust-line"
          >
            <ShieldCheck className="w-5 h-5 text-[#F77F11] mt-0.5 shrink-0" />
            <p className="text-[15px] text-neutral-700 leading-relaxed">
              {t('trusted.trustLine')}
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.4, delay: 0.05 }}
            className="flex items-start gap-3 rounded-2xl border border-orange-200 bg-orange-50/40 px-5 py-4"
            data-testid="trusted-reassurance"
          >
            <Sparkles className="w-5 h-5 text-[#F77F11] mt-0.5 shrink-0" />
            <p className="text-[15px] text-neutral-800 leading-relaxed font-medium">
              {t('trusted.reassurance')}
            </p>
          </motion.div>
        </div>

        {/* Business outcomes block */}
        <div className="mt-20 lg:mt-24">
          <div className="max-w-2xl">
            <p className="kx-eyebrow">{t('trusted.outcomesEyebrow')}</p>
            <h3 className="mt-3 text-3xl sm:text-4xl font-bold text-neutral-900 tracking-[-0.02em] leading-[1.1]">{t('trusted.outcomesTitle')}</h3>
            <p className="mt-4 text-neutral-600 text-[15.5px] leading-relaxed">{t('trusted.outcomesBody')}</p>
          </div>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {outcomes.map((o, i) => {
              const I = OUTCOME_ICONS[i] || Sparkles;
              return (
                <motion.div
                  key={o.title}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-50px' }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  whileHover={{ y: -3 }}
                  className="group rounded-2xl border border-neutral-200 bg-white p-6 hover:border-[#F77F11] hover:shadow-[0_24px_60px_-30px_rgba(247,127,17,0.35)] transition-all duration-300 flex flex-col"
                  data-testid={`outcome-card-${i}`}
                >
                  <span className="inline-flex w-11 h-11 rounded-xl bg-orange-50 items-center justify-center group-hover:bg-[#F77F11] transition-colors duration-300">
                    <I className="w-5 h-5 text-[#F77F11] group-hover:text-white transition-colors duration-300" />
                  </span>
                  <p className="mt-5 text-neutral-900 font-semibold text-[16px] tracking-[-0.005em] leading-snug">{o.title}</p>
                  <p className="mt-1.5 text-neutral-600 text-[15px] leading-relaxed flex-1">{o.subtitle}</p>

                  {o.cross && (
                    <div className="mt-5 pt-4 border-t border-neutral-100">
                      {o.soon ? (
                        <span
                          className="inline-flex items-center gap-1 text-[12px] font-semibold text-neutral-600 bg-neutral-100 border border-neutral-200 rounded-full px-2.5 py-1"
                          data-testid={`outcome-cross-${i}`}
                        >
                          {o.cross}
                          <span className="text-[10.5px] tracking-[0.18em] uppercase font-bold text-neutral-500 ml-1">
                            {lang === 'es' ? 'Pronto' : 'Soon'}
                          </span>
                        </span>
                      ) : (
                        <Link
                          to={o.crossHref}
                          className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#F77F11] hover:text-[#d8651a] bg-orange-50 hover:bg-orange-100/70 border border-orange-200 rounded-full px-2.5 py-1 transition-colors"
                          data-testid={`outcome-cross-${i}`}
                        >
                          {o.cross}
                          <ArrowRight className="w-3 h-3" />
                        </Link>
                      )}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
